import { usePodLabel } from "@app/components/assistant/conversation/tool_validation/usePodLabel";
import { podTaskSourceIcons } from "@app/components/pod/tasks/sourceIcons";
import { inferProjectTaskSourceFromUrl } from "@app/lib/api/actions/servers/pod_tasks/source_utils";
import type { PodTasksCreateTasksInput } from "@app/lib/api/actions/servers/pod_tasks/types";
import type { OwnerCandidate } from "@app/lib/project_task/owner_resolution";
import {
  podAssigneeFor,
  resolveProposedOwner,
} from "@app/lib/project_task/owner_resolution";
import { POD_TASK_NO_ASSIGNEE_LABEL } from "@app/types/project_task";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import { Checkbox, Chip, Icon, LinkExternal01 } from "@dust-tt/sparkle";

interface PodTasksCreateValidationDetailsProps {
  input: PodTasksCreateTasksInput;
  owner: LightWorkspaceType;
  user: UserType;
  conversationId?: string | null;
}

/**
 * The assignee each task will actually get, from the same rule the writer
 * applies: a named Pod member, else the proposed owner when they are in the
 * Pod, else nobody. Never "No assignee" for a task that will be assigned.
 */
export function previewTaskAssignee(
  task: PodTasksCreateTasksInput["tasks"][number],
  {
    podMembers,
    currentUserSId,
  }: { podMembers: OwnerCandidate[]; currentUserSId: string }
): string {
  const proposedOwner = task.proposedOwner
    ? resolveProposedOwner(task.proposedOwner, {
        container: podMembers,
        workspace: podMembers,
        aliases: new Map(),
      })
    : null;
  const { assignee, unknownAssignee } = podAssigneeFor(
    { assigneeUserSId: task.userId, proposedOwner },
    podMembers
  );

  if (unknownAssignee) {
    return "Will not be created: the named assignee is not in this Pod";
  }
  if (assignee) {
    return `Assigned to ${assignee.sId === currentUserSId ? "you" : assignee.fullName}`;
  }
  if (!proposedOwner) {
    return POD_TASK_NO_ASSIGNEE_LABEL;
  }
  if (proposedOwner.resolution === "unclear") {
    return "Unassigned, owner unclear";
  }

  return `Unassigned, proposed owner ${proposedOwner.name ?? proposedOwner.email} (not in this Pod)`;
}

export function PodTasksCreateValidationDetails({
  input,
  owner,
  user,
  conversationId,
}: PodTasksCreateValidationDetailsProps) {
  const { podLabel, isPodLabelLoading, podMembers } = usePodLabel({
    owner,
    podConfigUri: input.dustPod?.uri,
    conversationId,
  });

  const taskCount = input.tasks.length;
  const doneCount = input.tasks.filter((task) => task.doneRationale).length;

  return (
    <div className="flex flex-col gap-3 pt-2">
      <p className="text-sm text-muted-foreground">
        {input.creatorType !== "agent" ? (
          <>
            Review the{" "}
            <span className="font-medium text-foreground">{taskCount}</span>{" "}
            task{taskCount === 1 ? "" : "s"} below before adding them to{" "}
            <span className="font-medium text-foreground">
              {isPodLabelLoading ? "Loading…" : podLabel}
            </span>
            .
          </>
        ) : (
          <>
            The agent wants to create{" "}
            <span className="font-medium text-foreground">{taskCount}</span>{" "}
            task{taskCount === 1 ? "" : "s"} in{" "}
            <span className="font-medium text-foreground">
              {isPodLabelLoading ? "Loading…" : podLabel}
            </span>
            .
          </>
        )}
        {doneCount > 0 && <> {doneCount} will be marked as done immediately.</>}
      </p>

      <div className="divide-y divide-separator overflow-hidden rounded-xl border border-separator bg-background">
        {input.tasks.map((task, index) => {
          const assigneeLabel = podMembers
            ? previewTaskAssignee(task, {
                podMembers,
                currentUserSId: user.sId,
              })
            : "Loading…";
          const isDone = Boolean(task.doneRationale);

          return (
            <div
              key={`${index}-${task.text}`}
              className="flex items-start gap-3 px-3 py-3"
            >
              <div className="mt-0.5 shrink-0">
                <Checkbox checked={isDone} disabled />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex flex-wrap items-start gap-2">
                  <p className="min-w-0 flex-1 break-words text-sm leading-5 text-foreground">
                    {task.text}
                  </p>
                  {isDone && <Chip size="xs" color="success" label="Done" />}
                </div>
                <p className="text-xs text-muted-foreground">{assigneeLabel}</p>
                {task.doneRationale && (
                  <p className="text-xs italic text-muted-foreground">
                    {task.doneRationale}
                  </p>
                )}
                {task.sources && task.sources.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {task.sources.map((source) => {
                      const inferred = inferProjectTaskSourceFromUrl({
                        url: source.url,
                        title: source.title,
                      });
                      const SourceIcon =
                        podTaskSourceIcons[inferred.sourceType];

                      return (
                        <a
                          key={source.url}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-foreground hover:bg-muted/80"
                        >
                          <Icon
                            size="xs"
                            visual={SourceIcon}
                            className="shrink-0"
                          />
                          <span className="truncate">{source.title}</span>
                          <LinkExternal01 className="h-3 w-3 shrink-0 opacity-60" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
